import React from 'react';
import { View, Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from '../usePantryName';
import { BottomSheet } from '../../weekly/v2/primitives/BottomSheet';
import { FoodTile } from '../FoodTile';
import { FreshnessPill } from '../FreshnessPill';
import { PantryIcon } from '../PantryIcon';
import { ZONE_META } from '../zoneMeta';
import { Paprika, PaprikaFreshness } from '../../../styles/theme';
import { InventoryItem } from '../../../redux/inventorySlice';
import { freshnessOf, daysUntilExpiry } from '../../../services/pantryFreshnessService';
import { PantryActions } from '../usePantryActions';

interface Props {
  visible: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  actions: PantryActions;
  onMove: () => void;
}

export function ItemSheet({ visible, onClose, item, actions, onMove }: Props) {
  const { t } = useTranslation();
  const pantryName = usePantryName();

  if (!item) return null;

  const freshness = freshnessOf(item);
  const freshnessDays = daysUntilExpiry(item);
  const zoneMeta = ZONE_META[item.location || 'fridge'];

  const ActionButton = ({
    icon,
    label,
    bgColor,
    textColor,
    onPress,
  }: {
    icon: React.ReactNode;
    label: string;
    bgColor: string;
    textColor: string;
    onPress: () => void;
  }) => (
    <Pressable
      style={[styles.actionButton, { backgroundColor: bgColor }]}
      onPress={() => {
        onPress();
        onClose();
      }}
    >
      <View style={styles.actionContent}>
        <View>{icon}</View>
        <Text style={[styles.actionLabel, { color: textColor }]}>{label}</Text>
      </View>
    </Pressable>
  );

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      <View style={styles.container}>
        {/* Header row: FoodTile + name + FreshnessPill */}
        <View style={styles.header}>
          <FoodTile category={item.category} name={item.name} size={46} radius={13} />
          <View style={styles.headerText}>
            <Text style={styles.itemName}>{pantryName(item)}</Text>
            <View style={{ marginTop: 5 }}>
              <FreshnessPill state={freshness} days={freshnessDays} compact={false} />
            </View>
          </View>
        </View>

        {/* 2x2 Action grid */}
        <View style={styles.actionGrid}>
          <ActionButton
            icon={<PantryIcon icon={{ lib: 'mci', name: 'check' }} size={17} color={PaprikaFreshness.fresh.text} />}
            label={t('pantry.item.stillGood')}
            bgColor={PaprikaFreshness.fresh.bg}
            textColor={PaprikaFreshness.fresh.text}
            onPress={() => actions.stillGood(item)}
          />
          <ActionButton
            icon={<PantryIcon icon={{ lib: 'mci', name: 'refresh' }} size={17} color={Paprika.textSecondary} />}
            label={t('pantry.item.plusThreeDays')}
            bgColor={Paprika.surfaceLow}
            textColor={Paprika.textSecondary}
            onPress={() => actions.plusThreeDays(item)}
          />
          <ActionButton
            icon={<PantryIcon icon={{ lib: 'mci', name: 'check' }} size={17} color={Paprika.textSecondary} />}
            label={t('pantry.item.eatenDone')}
            bgColor={Paprika.surfaceLow}
            textColor={Paprika.textSecondary}
            onPress={() => actions.markEaten(item)}
          />
          <ActionButton
            icon={<PantryIcon icon={{ lib: 'mci', name: 'trash-can' }} size={17} color={PaprikaFreshness.expired.text} />}
            label={t('pantry.item.tossed')}
            bgColor={PaprikaFreshness.expired.bg}
            textColor={PaprikaFreshness.expired.text}
            onPress={() => actions.markTossed(item)}
          />
        </View>

        {/* Sealed vs opened. The single biggest lever on how long something
            keeps, and the pantry cannot see it from a photo. */}
        <Pressable
          style={styles.openedRow}
          accessibilityRole="switch"
          accessibilityState={{ checked: !!item.opened }}
          onPress={() => {
            actions.setOpened(item, !item.opened);
            onClose();
          }}
        >
          <View style={styles.openedText}>
            <Text style={styles.openedLabel}>{t('pantry.item.opened')}</Text>
            <Text style={styles.openedHint}>{t('pantry.item.openedHint')}</Text>
          </View>
          <View style={[styles.openedPip, item.opened && styles.openedPipOn]}>
            {item.opened && (
              <PantryIcon icon={{ lib: 'mci', name: 'check' }} size={14} color={Paprika.surfaceLowest} />
            )}
          </View>
        </Pressable>

        {/* Move button */}
        <Pressable
          style={styles.moveButton}
          onPress={onMove}
        >
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <PantryIcon icon={{ lib: 'mci', name: 'arrow-right' }} size={16} color={Paprika.textPrimary} />
            <Text style={styles.moveButtonLabel}>
              {t('pantry.item.move')} · <Text style={{ color: Paprika.textSecondary }}>{t(zoneMeta.labelKey)}</Text>
            </Text>
          </View>
        </Pressable>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  headerText: {
    flex: 1,
  },
  itemName: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  actionGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 9,
    marginBottom: 9,
  },
  actionButton: {
    width: '48%',
    paddingVertical: 14,
    borderRadius: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
  },
  openedRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceLow,
  },
  openedText: {
    flex: 1,
  },
  openedLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  openedHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  openedPip: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  openedPipOn: {
    backgroundColor: Paprika.primary,
  },
  moveButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    marginTop: 9,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.hairline,
  },
  moveButtonLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
});
